<template>
  <div class="daily-reports">
    <div class="page-header">
      <h1>Daily Reports</h1>
      <p>View daily sales, costs, and profit summaries</p>
    </div>

    <!-- Date Range Selector -->
    <el-card class="filter-card">
      <el-form inline>
        <el-form-item label="Date Range">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="to"
            start-placeholder="Start date"
            end-placeholder="End date"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            @change="loadDailyTotals"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadDailyTotals">Load Data</el-button>
          <el-button @click="debugData" size="small">Debug</el-button>
          <el-button @click="checkAug2" size="small">Check Aug 2</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="10" animated />
    </div>

    <!-- Daily Totals Table -->
    <el-card v-else-if="dailyTotals.length > 0" class="reports-card">
      <template #header>
        <div class="card-header">
          <span>Daily Summary</span>
          <el-button type="primary" size="small" @click="exportData">
            <el-icon><Download /></el-icon>
            Export
          </el-button>
        </div>
      </template>

      <el-table :data="dailyTotals" style="width: 100%">
        <el-table-column prop="_id" label="Date" width="120">
          <template #default="{ row }">
            {{ formatDate(row._id) }}
          </template>
        </el-table-column>
        
        <el-table-column label="Sales" width="120">
          <template #default="{ row }">
            <span class="positive">${{ formatNumber(row.totalSales || 0) }}</span>
          </template>
        </el-table-column>
        
        <el-table-column label="Costs" width="120">
          <template #default="{ row }">
            <span class="negative">${{ formatNumber(row.totalCosts || 0) }}</span>
          </template>
        </el-table-column>
        
        <el-table-column label="Profit" width="120">
          <template #default="{ row }">
            <span :class="{ 'positive': (row.profit || 0) >= 0, 'negative': (row.profit || 0) < 0 }">
              ${{ formatNumber(row.profit || 0) }}
            </span>
          </template>
        </el-table-column>
        
        <el-table-column label="Transactions" width="100">
          <template #default="{ row }">
            {{ getTransactionCount(row) }}
          </template>
        </el-table-column>
      </el-table>

      <!-- Summary Stats -->
      <div class="summary-stats">
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="stat-card">
              <h3>Total Sales</h3>
              <p class="positive">${{ formatNumber(totalSales) }}</p>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <h3>Total Costs</h3>
              <p class="negative">${{ formatNumber(totalCosts) }}</p>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <h3>Total Profit</h3>
              <p :class="{ 'positive': totalProfit >= 0, 'negative': totalProfit < 0 }">
                ${{ formatNumber(totalProfit) }}
              </p>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <h3>Average Daily</h3>
              <p :class="{ 'positive': avgDailyProfit >= 0, 'negative': avgDailyProfit < 0 }">
                ${{ formatNumber(avgDailyProfit) }}
              </p>
            </div>
          </el-col>
        </el-row>
      </div>
    </el-card>

    <!-- Empty State -->
    <el-card v-else class="empty-state">
      <div class="empty-content">
        <el-icon><Document /></el-icon>
        <h3>No Data Available</h3>
        <p>Select a date range to view daily reports</p>
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { Download, Document } from '@element-plus/icons-vue'
import { toast } from 'vue3-toastify'
import axios from 'axios'

export default {
  name: 'DailyReports',
  components: {
    Download,
    Document
  },
  setup() {
    const store = useStore()
    const loading = ref(false)
    const dateRange = ref([])
    const dailyTotals = ref([])

    const totalSales = computed(() => {
      return dailyTotals.value.reduce((sum, day) => sum + day.totalSales, 0)
    })

    const totalCosts = computed(() => {
      return dailyTotals.value.reduce((sum, day) => sum + day.totalCosts, 0)
    })

    const totalProfit = computed(() => {
      return totalSales.value - totalCosts.value
    })

    const avgDailyProfit = computed(() => {
      return dailyTotals.value.length > 0 ? totalProfit.value / dailyTotals.value.length : 0
    })

    const formatNumber = (num) => {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num)
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    const getTransactionCount = (day) => {
      // Return the transaction count from the daily totals
      return day.transactionCount || 0
    }

    const loadDailyTotals = async () => {
      if (!dateRange.value || dateRange.value.length !== 2) {
        toast.warning('Please select a date range')
        return
      }

      try {
        loading.value = true
        console.log('📅 Loading daily totals for range:', dateRange.value)
        
        // Test direct API call first using axios (which has interceptors)
        console.log('🧪 Testing direct API call...')
        try {
          const response = await axios.get(`/api/dashboard/daily-totals`, {
            params: {
              startDate: dateRange.value[0],
              endDate: dateRange.value[1]
            }
          })
          console.log('🧪 Direct API response:', response.data)
        } catch (error) {
          console.error('🧪 Direct API error:', error.response?.status, error.response?.statusText)
        }
        
        await store.dispatch('dashboard/fetchDailyTotals', {
          startDate: dateRange.value[0],
          endDate: dateRange.value[1]
        })
        
        const totals = store.getters['dashboard/dailyTotals']
        console.log('📊 Daily totals from store:', totals)
        dailyTotals.value = totals || []
        console.log('📊 Daily totals set in component:', dailyTotals.value)
        
        if (dailyTotals.value.length === 0) {
          console.log('⚠️ No data found. Checking overview...')
          const overview = store.getters['dashboard/overview']
          console.log('📋 Overview data:', overview)
          toast.info('No data found for the selected date range')
        } else {
          console.log('✅ Data loaded successfully:', dailyTotals.value.length, 'days')
        }
      } catch (error) {
        console.error('❌ Error loading daily totals:', error)
        toast.error('Failed to load daily totals')
        dailyTotals.value = []
      } finally {
        loading.value = false
      }
    }

    const debugData = async () => {
      console.log('🔍 Debugging Daily Reports Data:')
      console.log('📅 Date Range:', dateRange.value)
      console.log('📊 Daily Totals:', dailyTotals.value)
      console.log('💰 Total Sales:', totalSales.value)
      console.log('💸 Total Costs:', totalCosts.value)
      console.log('📈 Total Profit:', totalProfit.value)
      console.log('📋 Overview from store:', store.getters['dashboard/overview'])
      
      // Check specifically for Aug 2 data
      const aug2Data = dailyTotals.value.find(day => day._id === '2025-08-02')
      console.log('🔍 Aug 2 data found:', aug2Data)
      
      // Test API directly using axios
      try {
        const response = await axios.get('/api/dashboard/daily-totals', {
          params: {
            startDate: dateRange.value[0],
            endDate: dateRange.value[1]
          }
        })
        const data = response.data
        console.log('🧪 Direct API Response:', data)
        const apiAug2Data = data.find(day => day._id === '2025-08-02')
        console.log('🧪 Aug 2 data from API:', apiAug2Data)
        toast.info(`API returned ${data.length} days of data`)
      } catch (error) {
        console.error('🧪 API Error:', error)
        toast.error('API call failed')
      }
    }

    const checkAug2 = async () => {
      try {
        console.log('🔍 Specifically checking for Aug 2 data...')
        
        // Test with a specific date range that includes Aug 2
        const response = await axios.get('/api/dashboard/daily-totals', {
          params: {
            startDate: '2025-08-01',
            endDate: '2025-08-03'
          }
        })
        
        if (response.status === 200) {
          const data = response.data
          console.log('🧪 Aug 1-3 API Response:', data)
          
          const aug2Data = data.find(day => day._id === '2025-08-02')
          if (aug2Data) {
            console.log('✅ Aug 2 data found:', aug2Data)
            toast.success(`Aug 2: Sales $${aug2Data.totalSales}, Costs $${aug2Data.totalCosts}, Profit $${aug2Data.profit}`)
          } else {
            console.log('❌ No Aug 2 data found in API response')
            toast.warning('No Aug 2 data found in API response')
          }
        } else {
          console.error('❌ API Error:', response.status, response.statusText)
          toast.error('API call failed')
        }
      } catch (error) {
        console.error('❌ Check Aug 2 failed:', error)
        toast.error('Check Aug 2 failed')
      }
    }

    const exportData = () => {
      // Simple CSV export
      const csvContent = [
        'Date,Sales,Costs,Profit,Transactions',
        ...dailyTotals.value.map(day => 
          `${day._id},${day.totalSales},${day.totalCosts},${day.profit},${getTransactionCount(day)}`
        )
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `daily-reports-${dateRange.value[0]}-${dateRange.value[1]}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      
      toast.success('Data exported successfully')
    }

    onMounted(() => {
      // Set default date range to include more recent days (including Aug 2)
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 10) // Extend to 10 days to ensure we capture Aug 2
      dateRange.value = [
        start.toISOString().split('T')[0],
        end.toISOString().split('T')[0]
      ]
      
      console.log('📅 Default date range set:', dateRange.value)
      
      // Load data after a short delay to ensure component is ready
      setTimeout(() => {
        loadDailyTotals()
      }, 100)
    })

    return {
      loading,
      dateRange,
      dailyTotals,
      totalSales,
      totalCosts,
      totalProfit,
      avgDailyProfit,
      formatNumber,
      formatDate,
      getTransactionCount,
      loadDailyTotals,
      exportData,
      debugData,
      checkAug2
    }
  }
}
</script>

<style scoped>
.daily-reports {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0 0 8px 0;
  font-size: 1.8rem;
  font-weight: 600;
  color: #2c3e50;
}

.page-header p {
  margin: 0;
  color: #7f8c8d;
}

.filter-card {
  margin-bottom: 20px;
  border-radius: 8px;
}

.reports-card {
  border-radius: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.loading-container {
  padding: 20px;
}

.empty-state {
  border-radius: 8px;
}

.empty-content {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.empty-content .el-icon {
  font-size: 4rem;
  margin-bottom: 20px;
  color: #c0c4cc;
}

.empty-content h3 {
  margin: 0 0 10px 0;
  font-size: 1.2rem;
  color: #2c3e50;
}

.empty-content p {
  margin: 0;
  color: #7f8c8d;
}

.summary-stats {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

.stat-card {
  text-align: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-card h3 {
  margin: 0 0 10px 0;
  font-size: 0.9rem;
  color: #7f8c8d;
  font-weight: 500;
}

.stat-card p {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.positive {
  color: #67c23a;
  font-weight: 600;
}

.negative {
  color: #f56c6c;
  font-weight: 600;
}

@media (max-width: 768px) {
  .summary-stats .el-col {
    margin-bottom: 15px;
  }
}
</style> 